import { useNavigate } from 'react-router-dom';
import ROUTES from '../../../shared/constants/routes';
import { useSelector } from 'react-redux';
import { useService } from '../../../hooks/useService';
import { RootState } from '../../../redux/store';
import { useEffect } from 'react';
import { formatCurrency } from '../../../shared/utils/general.util';
import {
	CaretRight,
	CheckCircle,
	CurrencyDollarSimple,
	PiggyBank,
} from '@phosphor-icons/react';
import useAnnualLogic from '../../../hooks/useAnnualLogic';
import usePurchases from '../../../hooks/usePurchases';
import AnnualPDFPreview from '../../../assets/img/png/sat_annual_pdf_example.png';

interface AnnualPreviewHomeProps {
	page?: 'taxboard' | 'home';
}

const AnnualPreviewHome = ({ page }: AnnualPreviewHomeProps) => {
	const navigate = useNavigate();
	const { annualDeclarationOverview } = useSelector(
		(state: RootState) => state.tax,
	);

	const {
		userHasPendingActions,
		userCanViewDeclarationOverview,
		haveMoreThan400000Incomes,
		hasMultipleIssuersInRegime,
		haveMultipleRegimes,
	} = useAnnualLogic();
	const { userHasAnnualSubscription } = usePurchases();
	const { fetchAnnualDeclarationOverview } = useService();
	const { currentAnnualDeclaration } = useSelector(
		(state: RootState) => state.tax,
	);

	useEffect(() => {
		fetchAnnualDeclarationOverview();
	}, []);

	const annualIsMandatory =
		(haveMoreThan400000Incomes ||
			hasMultipleIssuersInRegime ||
			haveMultipleRegimes) &&
		!userHasAnnualSubscription;

	if (!userCanViewDeclarationOverview) return null;

	if (currentAnnualDeclaration && currentAnnualDeclaration.detail_pdf) {
		return (
			<div
				className='group bg-white/90 rounded-lg px-4 py-3 md:py-4 shadow-sm h-full cursor-pointer flex flex-col border border-white gap-4 md:gap-2 text-sm md:text-base relative overflow-hidden min-h-[160px]'
				onClick={() => {
					if (page === 'taxboard') {
						localStorage.setItem('annualPreviewHome', 'taxboard');
					} else if (page === 'home') {
						localStorage.setItem('annualPreviewHome', 'home');
					}
					navigate(ROUTES.ANNUAL_DECLARATION);
				}}
			>
				<div className='bg-gradient-to-b from-h-success-50/20 to-white h-[120px] w-full absolute top-0 left-0 ' />
				<div className='z-10'>
					<div className='flex items-center gap-2 justify-between'>
						<div className='font-bold tracking-tight leading-tight text-base'>
							Tu declaración anual 2024 está presentada
						</div>
						<CheckCircle
							size={24}
							weight='duotone'
							className='text-h-success-30'
						/>
					</div>
					<div className='opacity-70 flex items-center gap-1'>
						<CaretRight weight='bold' /> Ver detalle
					</div>
				</div>
				<div className='absolute bottom-0 left-0 w-full px-4'>
					<div className='w-full bg-white shadow h-[90px] flex flex-col gap-1 max-w-[350px] mx-auto text-sm fade-in-scale translate-y-[14px] group-hover:translate-y-[3px] transition-all duration-300 group-hover:scale-[1.01] group-hover:shadow-xl'>
						<img
							src={AnnualPDFPreview}
							alt='Acuse de presentación'
						/>
					</div>
				</div>
			</div>
		);
	}

	return (
		<div
			className='bg-white/90 rounded-lg px-4 py-3 md:py-4 shadow-sm h-full cursor-pointer flex flex-col border border-white gap-4 md:gap-2 text-sm md:text-base relative overflow-hidden'
			onClick={() => {
				if (page === 'taxboard') {
					localStorage.setItem('annualPreviewHome', 'taxboard');
				} else if (page === 'home') {
					localStorage.setItem('annualPreviewHome', 'home');
				}
				navigate(ROUTES.ANNUAL_DECLARATION);
			}}
		>
			<div className='bg-gradient-to-b from-h-primary-90/60 to-white h-[120px] w-full absolute top-0 left-0' />
			<div className='flex flex-col z-[1]'>
				<div className='font-bold tracking-tight leading-tight text-base'>
					{annualIsMandatory
						? '¡Es hora de presentar tu declaración anual 2024!'
						: 'Revisa el estado de tu declaración anual 2024'}
				</div>
				{userHasAnnualSubscription ? (
					userHasPendingActions ? (
						<div className='flex items-center gap-2 mt-1'>
							<div className='size-1.5 rounded-full bg-red-600 ' />
							<div className='font-light'>Tienes acciones pendientes</div>
						</div>
					) : (
						<div className='font-light flex items-center gap-2 leading-tight mt-1'>
							<div className='size-1.5 rounded-full bg-h-primary ' />
							Ver detalle
						</div>
					)
				) : (
					<div className='font-light flex items-center gap-2 leading-tight mt-1'>
						<div className='size-1.5 rounded-full bg-h-primary ' />
						{annualIsMandatory
							? 'Revisa tus datos y contrata ahora'
							: 'Ver detalle'}
					</div>
				)}
			</div>
			<div className='flex-1' />
			<div className='flex flex-col z-[1] opacity-80'>
				<div className='font-medium flex items-center gap-2'>
					<CurrencyDollarSimple weight='bold' />
					Ingreso identificado:{' '}
					<span className='font-light'>
						{formatCurrency(
							annualDeclarationOverview?.result.total_incomes || 0,
						)}
					</span>
				</div>
				<div className='font-medium flex items-center gap-2'>
					<PiggyBank weight='bold' />
					Deducciones personales:{' '}
					<span className='font-light'>
						{formatCurrency(
							annualDeclarationOverview?.result.total_deductions || 0,
						)}
					</span>
				</div>
			</div>
		</div>
	);
};

export default AnnualPreviewHome;
